import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useNavigate, Link } from 'react-router-dom';
import { Check, Eye, EyeOff } from 'lucide-react';
import type { Subscription } from '@supabase/supabase-js';

import { AuthShell } from './AuthShell';
import { resetSchema, type ResetInput } from './schemas';
import { supabase } from '../../lib/supabase';
import { logAudit } from '../../lib/audit';
import { cn } from '../../lib/cn';
import { useToast } from '../../components/Toast';
import styles from './auth.module.css';

type SessionState = 'checking' | 'valid' | 'expired';

function strengthOf(password: string): { pct: number; label: string; color: string } {
  let score = 0;
  if (password.length >= 8) score += 1;
  if (password.length >= 12) score += 1;
  if (/[a-z]/.test(password) && /[A-Z]/.test(password)) score += 1;
  if (/[0-9]/.test(password)) score += 1;
  if (/[^A-Za-z0-9]/.test(password)) score += 1;
  const pct = Math.min(100, score * 20);
  if (score <= 1) return { pct, label: 'חלשה', color: 'var(--err-red)' };
  if (score <= 3) return { pct, label: 'בינונית', color: 'var(--warn-yellow)' };
  return { pct, label: 'חזקה', color: 'var(--teal)' };
}

export function ResetScreen() {
  const navigate = useNavigate();
  const toast = useToast();
  const [sessionState, setSessionState] = useState<SessionState>('checking');
  const [showPassword, setShowPassword] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    let sub: Subscription | undefined;
    void supabase.auth.getSession().then(({ data }) => {
      if (!active) return;
      if (data.session) {
        setSessionState('valid');
      } else {
        // Recovery link sets session asynchronously — give it one tick.
        sub = supabase.auth.onAuthStateChange((event, session) => {
          if (event === 'PASSWORD_RECOVERY' || session) {
            setSessionState('valid');
          }
        }).data.subscription;
        setTimeout(() => {
          if (active) setSessionState((s) => (s === 'checking' ? 'expired' : s));
        }, 800);
      }
    });
    return () => {
      active = false;
      sub?.unsubscribe();
    };
  }, []);

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<ResetInput>({
    resolver: zodResolver(resetSchema),
    defaultValues: { password: '', confirmPassword: '' },
  });

  const passwordValue = watch('password');
  const strength = useMemo(() => strengthOf(passwordValue), [passwordValue]);
  const checks = useMemo(
    () => ({
      length: passwordValue.length >= 8,
      letter: /[a-zA-Z]/.test(passwordValue),
      digit: /[0-9]/.test(passwordValue),
    }),
    [passwordValue],
  );

  const onSubmit = handleSubmit(async ({ password }) => {
    setFormError(null);
    const { error } = await supabase.auth.updateUser({ password });
    if (error) {
      setFormError('לא הצלחנו לשמור סיסמה — נסה שוב');
      return;
    }
    await logAudit({ action: 'auth.password_reset_completed' });
    toast.success('הסיסמה אופסה');
    navigate('/auth/login');
  });

  if (sessionState === 'checking') {
    return (
      <AuthShell
        title="אפס סיסמה"
        brandSide={{
          title: 'בודק קישור...',
          body: 'רגע אחד, מאמת את הקישור שקיבלת באימייל.',
        }}
      >
        <p className={styles.sub}>בודק...</p>
      </AuthShell>
    );
  }

  if (sessionState === 'expired') {
    return (
      <AuthShell
        title="הקישור פג תוקף"
        subtitle="קישורי איפוס תקפים לשעה אחת בלבד"
        brandSide={{
          title: 'בקש קישור חדש',
          body: 'נשלח לך קישור חדש לאימייל. הישן כבר לא בתוקף.',
        }}
      >
        <p className={styles.formError}>הקישור פג תוקף או שכבר היה בשימוש.</p>
        <Link to="/auth/forgot" className={styles.primary}>
          בקש קישור חדש
        </Link>
      </AuthShell>
    );
  }

  return (
    <AuthShell
      title="אפס סיסמה"
      subtitle="הסיסמה החדשה תחליף את הקודמת בכל המכשירים"
      brandSide={{
        title: 'בחר סיסמה חזקה',
        body: 'לפחות 8 תווים, אות וספרה. סימן מיוחד עוזר לאבטחה.',
      }}
    >
      <form className={styles.form} onSubmit={onSubmit} noValidate>
        <div className={styles.field}>
          <label htmlFor="reset-password">סיסמה חדשה</label>
          <div className={styles.fieldInput}>
            <input
              id="reset-password"
              type={showPassword ? 'text' : 'password'}
              dir="ltr"
              autoComplete="new-password"
              className={cn(styles.input, errors.password && styles.invalid)}
              {...register('password')}
            />
            <button
              type="button"
              className={styles.eye}
              onClick={() => { setShowPassword((v) => !v); }}
              aria-label={showPassword ? 'הסתר סיסמה' : 'הצג סיסמה'}
            >
              {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
            </button>
          </div>
          {passwordValue ? (
            <div className={styles.strength} aria-live="polite">
              <div className={styles.strengthBar}>
                <div
                  className={styles.strengthFill}
                  style={{ inlineSize: `${String(strength.pct)}%`, background: strength.color }}
                />
              </div>
              <small style={{ color: strength.color }}>{strength.label}</small>
            </div>
          ) : null}
          {errors.password ? <p className={styles.error}>{errors.password.message}</p> : null}
        </div>

        <div className={styles.field}>
          <label htmlFor="reset-confirm">אימות סיסמה</label>
          <input
            id="reset-confirm"
            type={showPassword ? 'text' : 'password'}
            dir="ltr"
            autoComplete="new-password"
            className={cn(styles.input, errors.confirmPassword && styles.invalid)}
            {...register('confirmPassword')}
          />
          {errors.confirmPassword ? (
            <p className={styles.error}>{errors.confirmPassword.message}</p>
          ) : null}
        </div>

        <ul className={styles.checksList}>
          <li className={cn(styles.checkItem, checks.length && styles.met)}>
            <Check size={12} /> לפחות 8 תווים
          </li>
          <li className={cn(styles.checkItem, checks.letter && styles.met)}>
            <Check size={12} /> אות
          </li>
          <li className={cn(styles.checkItem, checks.digit && styles.met)}>
            <Check size={12} /> ספרה
          </li>
        </ul>

        {formError ? <p className={styles.formError}>{formError}</p> : null}

        <button type="submit" className={styles.primary} disabled={isSubmitting}>
          <Check size={14} /> אפס
        </button>
      </form>
    </AuthShell>
  );
}
